import { useEffect, useRef } from 'react';
import { getDbSupabase } from '../lib/supabase';

/** Coalesce Graph webhook bursts (ticket + messages + ack) into one quiet refresh. */
const DEBOUNCE_MS = 800;

type Options = {
  enabled?: boolean;
  onChange?: () => void;
  channelName?: string;
};

/**
 * Live-refresh the Tickets page when crm.tickets / crm.ticket_messages change
 * (Graph mail webhook + inbox-sync write those rows).
 */
export function useTicketInboxRealtime(options: Options = {}) {
  const { enabled = true, onChange, channelName = 'crm-tickets-live' } = options;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const schedule = () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        onChangeRef.current?.();
      }, DEBOUNCE_MS);
    };

    const db = getDbSupabase('crm');
    let channel = db
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'crm', table: 'tickets' },
        schedule,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'crm', table: 'ticket_messages' },
        schedule,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'crm', table: 'ticket_attachments' },
        schedule,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'crm', table: 'ticket_folders' },
        schedule,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'crm', table: 'ticket_tag_links' },
        schedule,
      );

    channel = channel.subscribe();

    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      void db.removeChannel(channel);
    };
  }, [enabled, channelName]);
}
